import BrokenImage from '@mui/icons-material/BrokenImage';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Modal,
    Skeleton,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { AttachmentAsset } from '../../../../models/standard/AttachmentAsset';
import { getPrimaryDate } from '../../../../utils/attachmentUtils';
import { immersightBlue, immersightBlue75 } from '../../../../utils/colors';
import { PLATFORM_URL } from '../../../../utils/constants';

interface AttachmentImageContentProps {
  data: AttachmentAsset;
  onDelete: () => void;
}

const ImageNotFoundPlaceholder = ({ height = 180 }: { height?: number | string }) => (
  <Box
    sx={{
      width: '100%',
      height: height,
      borderRadius: 3,
      overflow: 'hidden',
      position: 'relative',
      backgroundColor: '#f0f0f0',
    }}
  >
    <Skeleton variant="rectangular" width="100%" height="100%" animation={false} />
    <Box
      sx={{
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#999',
      }}
    >
      <BrokenImage sx={{ fontSize: 40, mb: 1 }} />
      <Typography variant="caption" fontWeight="bold">
        Image not found
      </Typography>
    </Box>
  </Box>
);

export const AttachmentImageContent: React.FC<AttachmentImageContentProps> = ({
  data,
  onDelete,
}) => {
  const intl = useIntl();
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Loading States
  const [thumbError, setThumbError] = useState(false);
  const [fullImageError, setFullImageError] = useState(false);
  const [imgLoading, setImgLoading] = useState(true); 

  // Reset loading state whenever modal opens
  useEffect(() => {
    if (isModalOpen) {
      setImgLoading(true);
      setFullImageError(false);
    }
  }, [isModalOpen]);

  const getThumbUrl = () => (data.thumbnailUrl ? `${PLATFORM_URL}${data.thumbnailUrl}` : '');
  const getDownloadUrl = () => (data.downloadUrl ? `${PLATFORM_URL}${data.downloadUrl}` : '');

  // Helper to format date and decide label
  const renderDateInfo = () => {
    const { date, fromShooting } = getPrimaryDate(data);
    
    if (!date) return null;

    const dateStr = `${date.toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    })} ${date.toLocaleTimeString('de-DE', {
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    })}`;

    const labelId = fromShooting 
      ? 'AttachmentDrawer.image.shootingDate' // "Aufnahmedatum:"
      : 'AttachmentDrawer.image.creationDate'; // "Erstelldatum:" 
    
    const defaultMessage = fromShooting ? 'Aufnahmedatum:' : 'Erstelldatum:';

    return (
      <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem', mb: 0.5 }}>
        {intl.formatMessage({ id: labelId, defaultMessage })} {dateStr}
      </Typography>
    );
  };

  return (
    <>
      {/* --- DRAWER CARD CONTENT --- */}
      <Box sx={{ borderRadius: 2, p: 0, backgroundColor: '#ffffff' }}>
        {/* Title Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              width: 32, height: 32, borderRadius: '50%',
              backgroundColor: immersightBlue,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <ImageIcon sx={{ color: '#ffffff', fontSize: 20 }} />
          </Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700, color: '#333', fontSize: '1.1rem', wordBreak: 'break-word',
            }}
          >
            {data.fileName || intl.formatMessage({ id: 'AttachmentDrawer.image.untitled', defaultMessage: 'Bild' })}
          </Typography>
        </Box>

        {/* Metadata */}
        <Box sx={{ mb: 2 }}>
          {/* Dynamic Date Label */}
          {renderDateInfo()}
          
          {data.description && (
            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.9rem' }}>
              {data.description}
            </Typography>
          )}
        </Box>

        {/* Thumbnail */}
        <Box sx={{ mb: 3, cursor: 'pointer' }} onClick={() => setIsModalOpen(true)}>
          {thumbError || !data.thumbnailUrl ? (
            <ImageNotFoundPlaceholder height={180} />
          ) : (
            <Box
              component="img"
              src={getThumbUrl()}
              alt={data.fileName}
              onError={() => setThumbError(true)}
              sx={{
                width: '100%', height: 180, objectFit: 'cover',
                borderRadius: 3, display: 'block',
                transition: 'opacity 0.2s',
                '&:hover': { opacity: 0.9 },
              }}
            />
          )}
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5 }}>
          <Button
            variant="contained"
            startIcon={<OpenInNewIcon />}
            onClick={() => setIsModalOpen(true)}
            sx={{
              backgroundColor: immersightBlue,
              borderRadius: '18px',
              fontWeight: 'bold',
              padding: '8px 24px',
              boxShadow: 'none',
              textTransform: 'uppercase',
              '&:hover': { backgroundColor: immersightBlue75, boxShadow: 'none' },
            }}
          >
            <FormattedMessage id="AttachmentDrawer.openImage" defaultMessage="Bild öffnen" />
          </Button>
          <Button
            variant="contained"
            startIcon={<DeleteIcon />}
            onClick={onDelete}
            sx={{
              backgroundColor: immersightBlue,
              borderRadius: '18px',
              fontWeight: 'bold',
              padding: '8px 24px',
              boxShadow: 'none',
              textTransform: 'uppercase',
              '&:hover': { backgroundColor: immersightBlue75, boxShadow: 'none' },
            }}
          >
            <FormattedMessage id="AttachmentDrawer.deleteAttachment" defaultMessage="Attachment löschen" />
          </Button>
        </Box>
      </Box>

      {/* --- FULL SCREEN MODAL --- */}
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        aria-labelledby="modal-image-title"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box
          sx={{
            outline: 'none', position: 'relative',
            minWidth: 300, 
            minHeight: 200,
            maxWidth: '90vw', maxHeight: '90vh',
            borderRadius: 2, overflow: 'hidden',
            boxShadow: 24, bgcolor: 'background.paper',
            '&:hover .image-overlay': { opacity: (!imgLoading && !fullImageError) ? 0.4 : 0 },
          }}
        >
          {/* 1. Error State */}
          {fullImageError || !data.downloadUrl ? (
            <Box sx={{ width: 400, height: 300 }}>
              <ImageNotFoundPlaceholder height="100%" />
              <IconButton
                onClick={() => setIsModalOpen(false)}
                sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(255,255,255,0.8)' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          ) : (
            <>
              {/* 2. Loading Spinner */}
              {imgLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10, 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255,255,255,0.7)', 
                    borderRadius: '50%',
                    p: 1,
                  }}
                >
                  <CircularProgress size={40} sx={{ color: immersightBlue }} />
                </Box>
              )}

              {/* 3. The Image */}
              <Box
                component="img"
                src={getDownloadUrl()}
                onLoad={() => setImgLoading(false)}
                onError={() => {
                  setImgLoading(false);
                  setFullImageError(true);
                }}
                alt="Full size"
                sx={{ 
                  display: 'block', 
                  maxWidth: '90vw', 
                  maxHeight: '90vh', 
                  objectFit: 'contain' 
                }}
              />

              {/* 4. Close Button Overlay */}
              <Box
                className="image-overlay"
                sx={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  opacity: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  transition: 'opacity 0.2s ease-in-out',
                  display: 'flex', alignItems: 'start', justifyContent: 'flex-end', p: 1,
                  zIndex: 20
                }}
              >
                <IconButton
                  onClick={() => setIsModalOpen(false)}
                  sx={{ color: '#fff', backgroundColor: 'rgba(0,0,0,1.0)', '&:hover': { backgroundColor: 'rgba(0,0,0,1.0)' } }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* 5. Fallback Close Button */}
              {imgLoading && (
                <IconButton
                  onClick={() => setIsModalOpen(false)}
                  sx={{ 
                    position: 'absolute', 
                    top: 8, 
                    right: 8, 
                    color: '#000', 
                    bgcolor: 'rgba(255,255,255,0.6)',
                    zIndex: 20,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </>
          )}
        </Box>
      </Modal>
    </>
  );
};